/**
 * Converts a time duration in milliseconds to a human-readable duration.
 *
 * @param {number} durationInMs - The time duration in milliseconds.
 * @returns {string} The human-readable duration. The string is formatted as follows:
 * if days > 0, it appends "day(s)";
 * if hours > 0, it appends "hr(s)";
 * if minutes > 0, it appends "min";
 * if seconds > 0, it appends "sec";
 * if milliseconds > 0, it appends "ms".
 * Each unit is separated by a space. If the input is negative, the output string starts with a "-".
 */
export default function humanDuration(durationInMs) {
	let duration = durationInMs >= 0 ? '' : '-'
	const {days, hours, minutes, seconds, milliseconds} = durationByTimeUnits(Math.abs(durationInMs))
	if (days > 0) {
		duration += days + ` day${days > 1 ? 's' : ''} `
	}
	if (hours > 0) {
		duration += hours + ` hr${hours > 1 ? 's' : ''} `
	}
	if (minutes > 0) {
		duration += minutes + ' min '
	}
	if (seconds > 0) {
		duration += seconds + ' sec '
	}
	if (milliseconds > 0) {
		duration += milliseconds + ' ms'
	}
	return duration.trim()
}

/**
 * Converts a time duration in milliseconds to a short human-readable duration.
 *
 * @param {number} durationInMs - The time duration in milliseconds.
 * @returns {string} The human-readable duration.
 * if hours > 0, it appends "hh:";
 * it always appends "mm:";
 * it always appends "ss", padded to 2 digits with leading zeros.
 * If the time difference is less than -1000ms, the output string starts with a "-".
 * Don't display negative sign, if the time duration is not visible (-0:00)
 */
export function humanDurationShort(durationInMs) {
	durationInMs = parseInt(durationInMs)
	let duration = durationInMs > -1000 ? '' : '-'
	const {hours, minutes, seconds} = durationByTimeUnits(durationInMs)
	if (hours > 0) {
		duration += hours + ':'
	}
	const formattedMinutes = hours > 0 ? String(minutes).padStart(2, '0') : minutes
	duration += formattedMinutes + ':'
	duration += String(seconds).padStart(2, '0')
	return duration
}

/**
 * Breaks down a time duration in milliseconds into days, hours, minutes, seconds, and milliseconds.
 *
 * @param {number} durationInMs - The time duration in milliseconds.
 * @returns {Object} An object with properties for days, hours, minutes, seconds, and milliseconds.
 *                   Each property represents the corresponding part of the time difference.
 *                   The values are non-negative integers.
 */
export function durationByTimeUnits(durationInMs) {
	durationInMs = Math.abs(durationInMs)
	const days = Math.floor(durationInMs / 1000 / 60 / 60 / 24)
	durationInMs -= days * 1000 * 60 * 60 * 24
	const hours = Math.floor(durationInMs / 1000 / 60 / 60)
	durationInMs -= hours * 1000 * 60 * 60
	const minutes = Math.floor(durationInMs / 1000 / 60)
	durationInMs -= minutes * 1000 * 60
	const seconds = Math.floor(durationInMs / 1000)
	durationInMs -= seconds * 1000
	const milliseconds = Math.floor(durationInMs)
	return {days, hours, minutes, seconds, milliseconds}
}
